<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { watchDebounced } from '@vueuse/core';

import ConversationAPI from 'dashboard/api/inbox/conversation';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  message: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['close']);

const MAX_TARGETS = 10;
const { t } = useI18n();
const store = useStore();

const query = ref('');
const conversations = ref([]);
const selectedIds = ref([]);
const isLoading = ref(false);
const isForwarding = ref(false);
const searchError = ref('');

const sourceConversationId = computed(
  () => props.message.conversation_id ?? props.message.conversationId
);
const inboxId = computed(() => props.message.inbox_id ?? props.message.inboxId);
const canForward = computed(
  () => selectedIds.value.length > 0 && !isForwarding.value
);

const senderFor = conversation => conversation.meta?.sender || {};
const targetName = conversation => {
  const sender = senderFor(conversation);
  return (
    sender.name ||
    sender.phone_number ||
    sender.identifier ||
    t('CONVERSATION.CONTEXT_MENU.FORWARD_CONVERSATION', {
      id: conversation.id,
    })
  );
};
const targetDetail = conversation => {
  const sender = senderFor(conversation);
  return sender.phone_number || sender.identifier || `#${conversation.id}`;
};

const loadConversations = async () => {
  if (!inboxId.value) return;

  isLoading.value = true;
  searchError.value = '';
  try {
    const response = await ConversationAPI.searchForwardTargets({
      inboxId: inboxId.value,
      q: query.value.trim(),
    });
    conversations.value = (response.data?.data?.payload || []).filter(
      conversation =>
        Number(conversation.id) !== Number(sourceConversationId.value)
    );
  } catch (error) {
    conversations.value = [];
    searchError.value = t('CONVERSATION.CONTEXT_MENU.FORWARD_SEARCH_ERROR');
  } finally {
    isLoading.value = false;
  }
};

const isSelected = conversationId =>
  selectedIds.value.includes(Number(conversationId));

const toggleConversation = conversationId => {
  const normalizedId = Number(conversationId);
  if (isSelected(normalizedId)) {
    selectedIds.value = selectedIds.value.filter(id => id !== normalizedId);
    return;
  }
  if (selectedIds.value.length < MAX_TARGETS) {
    selectedIds.value = [...selectedIds.value, normalizedId];
  }
};

const forwardMessage = async () => {
  if (!canForward.value) return;

  isForwarding.value = true;
  try {
    const result = await store.dispatch('forwardWhatsappMessage', {
      conversationId: sourceConversationId.value,
      messageId: props.message.id,
      targetConversationIds: selectedIds.value,
    });
    const forwardedCount = result.forwarded?.length || 0;
    const failedCount = result.failed?.length || 0;

    if (failedCount) {
      useAlert(
        t('CONVERSATION.CONTEXT_MENU.FORWARD_PARTIAL', {
          forwarded: forwardedCount,
          failed: failedCount,
        })
      );
    } else {
      useAlert(
        t('CONVERSATION.CONTEXT_MENU.FORWARD_SUCCESS', {
          count: forwardedCount,
        })
      );
    }
    emit('close');
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('CONVERSATION.CONTEXT_MENU.FORWARD_ERROR')
    );
  } finally {
    isForwarding.value = false;
  }
};

watchDebounced(query, loadConversations, { debounce: 300, maxWait: 700 });
onMounted(loadConversations);
</script>

<template>
  <div class="flex max-h-[38rem] min-h-[28rem] flex-col gap-4 p-6">
    <div>
      <h3 class="mb-1 text-base font-medium text-n-slate-12">
        {{ t('CONVERSATION.CONTEXT_MENU.FORWARD_TITLE') }}
      </h3>
      <p class="m-0 text-sm text-n-slate-10">
        {{ t('CONVERSATION.CONTEXT_MENU.FORWARD_DESCRIPTION') }}
      </p>
    </div>

    <Input
      v-model="query"
      autofocus
      :placeholder="t('CONVERSATION.CONTEXT_MENU.FORWARD_SEARCH_PLACEHOLDER')"
    />

    <div
      class="flex min-h-0 flex-1 flex-col overflow-y-auto rounded-lg border border-n-weak"
    >
      <div
        v-if="isLoading"
        class="flex flex-1 items-center justify-center p-8 text-sm text-n-slate-10"
      >
        {{ t('CONVERSATION.CONTEXT_MENU.FORWARD_LOADING') }}
      </div>
      <div
        v-else-if="searchError"
        class="flex flex-1 items-center justify-center p-8 text-center text-sm text-n-ruby-9"
      >
        {{ searchError }}
      </div>
      <div
        v-else-if="!conversations.length"
        class="flex flex-1 items-center justify-center p-8 text-center text-sm text-n-slate-10"
      >
        {{ t('CONVERSATION.CONTEXT_MENU.FORWARD_EMPTY') }}
      </div>
      <template v-else>
        <button
          v-for="conversation in conversations"
          :key="conversation.id"
          type="button"
          class="flex w-full items-center gap-3 border-0 border-b border-n-weak bg-transparent px-3 py-3 text-left last:border-b-0 hover:bg-n-alpha-2"
          @click="toggleConversation(conversation.id)"
        >
          <Avatar
            :name="targetName(conversation)"
            :src="senderFor(conversation).thumbnail"
            :size="36"
            rounded-full
          />
          <span class="min-w-0 flex-1">
            <span class="block truncate text-sm font-medium text-n-slate-12">
              {{ targetName(conversation) }}
            </span>
            <span class="block truncate text-xs text-n-slate-10">
              {{ targetDetail(conversation) }}
            </span>
          </span>
          <span
            class="flex size-5 items-center justify-center rounded border"
            :class="
              isSelected(conversation.id)
                ? 'border-n-brand bg-n-brand text-white'
                : 'border-n-strong text-transparent'
            "
          >
            <span class="i-lucide-check size-3.5" />
          </span>
        </button>
      </template>
    </div>

    <div class="flex items-center justify-between gap-3">
      <p class="m-0 text-xs text-n-slate-10">
        {{
          t('CONVERSATION.CONTEXT_MENU.FORWARD_SELECTED', {
            count: selectedIds.length,
            max: MAX_TARGETS,
          })
        }}
      </p>
      <div class="flex gap-2">
        <NextButton
          slate
          faded
          :label="t('CONVERSATION.CONTEXT_MENU.FORWARD_CANCEL')"
          @click="emit('close')"
        />
        <NextButton
          icon="i-lucide-forward"
          :label="t('CONVERSATION.CONTEXT_MENU.FORWARD_ACTION')"
          :disabled="!canForward"
          :is-loading="isForwarding"
          @click="forwardMessage"
        />
      </div>
    </div>
  </div>
</template>
